import React, { memo, useCallback, useMemo } from "react";
import { IconButton, SxProps, Theme } from "@mui/material";

import UtfButton from "./UtfButton/UtfButton";

type ActionButtonProps = {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
  testId?: string;
  sx?: SxProps<Theme>;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
  size?: "small" | "medium" | "large";
};

/**
 * A reusable action button component with consistent styling and accessibility features
 */
export const ActionButton: React.FC<ActionButtonProps> = memo(
  ({
    onClick,
    disabled = false,
    children,
    icon,
    testId,
    sx,
    variant = "outlined",
    color = "primary",
    size = "small",
  }) => {
    const commonButtonStyles: SxProps<Theme> = useMemo(
      () => [
        {
          borderRadius: "10px",
          textTransform: "capitalize",
          "&:hover": {
            cursor: "pointer",
            backgroundColor: "primary.main",
            color: "common.white",
          },
          "&:focus": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ],
      [sx]
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
        }
      },
      [onClick]
    );

    return (
      <UtfButton
        variant={variant}
        size={size}
        color={color}
        sx={commonButtonStyles}
        disabled={disabled}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        data-testid={testId}
        aria-label={typeof children === "string" ? children : "Action button"}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        {icon}
        {children}
      </UtfButton>
    );
  }
);

ActionButton.displayName = "ActionButton";
